import type {
  OrderStats,
  AdminDashboard, AdminOrderAction, AdminOrderDetail, AuditEntry, Beneficiary, Company, CompanyDocKind, CompanyUser,
  DocumentRef, KybCase, LoginResponse, Me, Nature, NetworkInfo, Notification, OrderDetail, OrderSummary, Page,
  PixCharge, Quote, ReconciliationIssue, ReconciliationRun, Relationship, SettingKey, Setting, SettlementRail, Ubo,
  Activity, ApiErrorBody, ApiKey, AccessRequest, Lead, PostalAddress } from "./types";

export const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
  get isUnauthorized() { return this.status === 401; }
}

type Query = Record<string, string | number | boolean | undefined | null>;

function qs(q?: Query): string {
  if (!q) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
}

interface RequestOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  form?: FormData;
  query?: Query;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

const unauthorizedListeners = new Set<() => void>();
export function onUnauthorized(fn: () => void): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

async function parseError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    body = null;
  }
  const code = body?.error?.code ?? `http_${res.status}`;
  const message = body?.error?.message ?? defaultMessage(res.status);
  return new ApiError(res.status, code, message, body?.error?.details);
}

function defaultMessage(status: number): string {
  if (status === 401) return "Sua sessão expirou. Entre de novo.";
  if (status === 403) return "Você não tem permissão para esta ação.";
  if (status === 404) return "Não encontramos o que você procurou.";
  if (status === 429) return "Muitas tentativas. Aguarde alguns minutos.";
  if (status >= 500) return "Nosso servidor falhou. Tente de novo em instantes.";
  return "Algo deu errado. Tente de novo.";
}

export async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") headers["x-fxp-csrf"] = "1";
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${qs(opts.query)}`, { method, headers, body, credentials: "include", signal: opts.signal });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, "network_error", "Sem conexão com o servidor. Verifique sua internet e tente de novo.");
  }
  if (!res.ok) {
    const err = await parseError(res);
    if (err.status === 401 && !path.startsWith("/auth/")) unauthorizedListeners.forEach((fn) => fn());
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/** Aceita `[...]` ou `{items:[...]}`. */
export function asList<T>(x: unknown): T[] {
  if (Array.isArray(x)) return x as T[];
  if (x && typeof x === "object" && Array.isArray((x as { items?: unknown }).items)) return (x as { items: T[] }).items;
  return [];
}

export function fileUrl(path: string): string {
  return `${API_BASE}${path}`;
}

// ---------- Auth ----------
export const auth = {
  register: (data: { email: string; password: string; name: string }) => request<{ userId: string }>("/auth/register", { method: "POST", body: data }),
  login: (data: { email: string; password: string }) => request<LoginResponse>("/auth/login", { method: "POST", body: data }),
  totpEnroll: (data: { pendingToken: string; code: string }) => request<{ ok: true }>("/auth/totp/enroll", { method: "POST", body: data }),
  totpVerify: (data: { pendingToken: string; code: string }) => request<{ ok: true }>("/auth/totp/verify", { method: "POST", body: data }),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  me: (signal?: AbortSignal) => request<Me>("/me", { signal }),
  forgotPassword: (email: string) => request<{ ok: true }>("/auth/password/forgot", { method: "POST", body: { email } }),
  resetPassword: (token: string, password: string) => request<{ ok: true }>("/auth/password/reset", { method: "POST", body: { token, password } }),
  changePassword: (currentPassword: string, newPassword: string) => request<{ ok: true }>("/auth/password/change", { method: "POST", body: { currentPassword, newPassword } }),
  forgotTotp: (email: string) => request<{ ok: true }>("/auth/totp/forgot", { method: "POST", body: { email } }),
  resetTotp: (token: string, password: string) => request<{ ok: true }>("/auth/totp/reset", { method: "POST", body: { token, password } }),
  reconfigureTotp: (password: string) => request<{ ok: true }>("/auth/totp/reconfigure", { method: "POST", body: { password } }),
};

// ---------- Empresa e KYB ----------
export const companies = {
  current: () => request<{ company: Company }>("/companies/current").then((r) => r.company),
  create: (cnpj: string) => request<{ company: Company }>("/companies", { method: "POST", body: { cnpj }, idempotencyKey: newIdempotencyKey() }),
  update: (data: { tradeName?: string; address?: PostalAddress; phone?: string; website?: string; companyType?: string; annualRevenueUsdCents?: string; incorporationDate?: string; activity?: Activity }) => request<{ company: Company }>("/companies/current", { method: "PATCH", body: data }),
  setUbos: (ubos: Ubo[]) => request<unknown>("/companies/current/ubos", { method: "PUT", body: ubos }),
  uploadDocument: (file: File, kind: CompanyDocKind) => {
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    return request<{ document: DocumentRef }>("/companies/current/documents", { method: "POST", form });
  },
  submitKyb: (acceptedTermsVersion: string) => request<{ kybCase: KybCase }>("/companies/current/kyb/submit", { method: "POST", body: { acceptedTermsVersion }, idempotencyKey: newIdempotencyKey() }),
  kyb: () => request<{ kybCase: KybCase | null; history?: KybCase[] }>("/companies/current/kyb"),
  users: () => request<unknown>("/companies/current/users").then((r) => asList<CompanyUser>(r)),
  createUser: (data: { email: string; name: string; role: "owner" | "finance" | "viewer"; password: string }) => request<unknown>("/companies/current/users", { method: "POST", body: data }),
  apiKeys: () => request<{ apiKeys: ApiKey[] }>("/companies/current/api-keys").then((r) => r.apiKeys),
  createApiKey: (name: string) => request<{ apiKey: ApiKey; secret: string }>("/companies/current/api-keys", { method: "POST", body: { name } }),
  revokeApiKey: (id: string) => request<void>(`/companies/current/api-keys/${id}`, { method: "DELETE" }),
};

// ---------- Fornecedores ----------
export const beneficiaries = {
  list: () => request<unknown>("/beneficiaries").then((r) => asList<Beneficiary>(r)),
  get: (id: string) => request<Beneficiary | { beneficiary: Beneficiary }>(`/beneficiaries/${id}`).then((r) => ("beneficiary" in r ? r.beneficiary : r)),
  create: (data: { legalName: string; country: string; address: string; document?: string; relationship: Relationship; rail: SettlementRail }) =>
    request<Beneficiary | { beneficiary: Beneficiary }>("/beneficiaries", { method: "POST", body: data, idempotencyKey: newIdempotencyKey() }).then((r) => ("beneficiary" in r ? r.beneficiary : r)),
  update: (id: string, data: Partial<{ legalName: string; country: string; address: string; document: string; relationship: Relationship; rail: SettlementRail }>) =>
    request<Beneficiary | { beneficiary: Beneficiary }>(`/beneficiaries/${id}`, { method: "PATCH", body: data }).then((r) => ("beneficiary" in r ? r.beneficiary : r)),
  networks: () => request<unknown>("/networks").then((r) => asList<NetworkInfo>(r)),
};

// ---------- Cotação e pedidos ----------
export const quotes = {
  create: (data: { beneficiaryId: string; usdCents: string; nature: Nature }) => request<{ quote: Quote }>("/quotes", { method: "POST", body: data }),
};

export const orders = {
  create: (data: { quoteId: string; invoiceDocumentId: string; reference?: string }, idempotencyKey: string) =>
    request<{ order: OrderSummary; pixCharge: PixCharge }>("/orders", { method: "POST", body: data, idempotencyKey }),
  stats: (signal?: AbortSignal) => request<OrderStats>("/orders/stats", { signal }),
  list: (q?: { state?: string; from?: string; to?: string; q?: string; page?: number }) => request<Page<OrderSummary> | OrderSummary[]>("/orders", { query: q }).then((r) => (Array.isArray(r) ? { items: r, total: r.length } : r)),
  get: (id: string, signal?: AbortSignal) => request<OrderDetail>(`/orders/${id}`, { signal }),
  cancel: (id: string) => request<unknown>(`/orders/${id}/cancel`, { method: "POST", idempotencyKey: newIdempotencyKey() }),
  nps: (id: string, score: number) => request<unknown>(`/orders/${id}/nps`, { method: "POST", body: { score } }),
  documentUrl: (id: string, kind: "receipt" | "fx_contract" | "invoice") => fileUrl(`/orders/${id}/documents/${kind}`),
  exportCsvUrl: (q?: { from?: string; to?: string }) => fileUrl(`/orders/export.csv${qs(q)}`),
  statementUrl: (yyyymm: string) => fileUrl(`/statements/${yyyymm}.pdf`),
};

export const documents = {
  upload: (file: File, kind = "invoice") => {
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    return request<{ document: DocumentRef }>("/documents", { method: "POST", form });
  },
  url: (id: string) => fileUrl(`/documents/${id}`),
};

export const push = {
  status: () => request<{ enabled: boolean; publicKey: string | null; subscriptions: number }>("/notifications/push"),
  subscribe: (data: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string }) => request<{ id: string }>("/notifications/push/subscribe", { method: "POST", body: data }),
  unsubscribe: (endpoint: string) => request<void>("/notifications/push/subscribe", { method: "DELETE", body: { endpoint } }),
};

export const notifications = {
  list: () => request<unknown>("/notifications").then((r) => asList<Notification>(r)),
  read: (id: string) => request<unknown>(`/notifications/${id}/read`, { method: "POST" }),
  readAll: () => request<{ updated: number }>("/notifications/read-all", { method: "POST" }),
};

// ---------- Sandbox ----------
export const sandbox = {
  pixPay: (orderId: string, paidCents?: string) => request<unknown>(`/sandbox/orders/${orderId}/pix/pay`, { method: "POST", body: paidCents ? { paidCents } : {} }),
  pixExpire: (orderId: string) => request<unknown>(`/sandbox/orders/${orderId}/pix/expire`, { method: "POST", body: {} }),
  kybDecide: (caseId: string, status: string) => request<unknown>(`/sandbox/kyb/${caseId}/decide`, { method: "POST", body: { status } }),
};

// ---------- Toca (backoffice) ----------
export const admin = {
  dashboard: (q?: { from?: string; to?: string }) => request<AdminDashboard>("/admin/dashboard", { query: q }),
  leads: () => request<unknown>("/admin/leads").then((r) => asList<Lead>(r)),
  leadContacted: (id: string, note: string) => request<unknown>(`/admin/leads/${id}/contacted`, { method: "POST", body: { note } }),
  users: (access: "pending" | "approved" | "rejected") => request<unknown>("/admin/users", { query: { access } }).then((r) => asList<AccessRequest>(r)),
  userAccess: (id: string, decision: "approved" | "rejected", note: string) => request<unknown>(`/admin/users/${id}/access`, { method: "POST", body: { decision, note } }),
  kybCases: (status?: string) => request<unknown>("/admin/kyb-cases", { query: { status } }).then((r) => asList<KybCase>(r)),
  kybDecide: (id: string, decision: "approved" | "rejected" | "needs_more_info", note: string) => request<unknown>(`/admin/kyb-cases/${id}/decide`, { method: "POST", body: { decision, note }, idempotencyKey: newIdempotencyKey() }),
  beneficiaries: (status?: string) => request<unknown>("/admin/beneficiaries", { query: { status } }).then((r) => asList<Beneficiary>(r)),
  beneficiaryDecide: (id: string, decision: "active" | "blocked", note: string) => request<unknown>(`/admin/beneficiaries/${id}/decide`, { method: "POST", body: { decision, note }, idempotencyKey: newIdempotencyKey() }),
  orders: (q?: { state?: string; companyId?: string; exception?: boolean; page?: number }) => request<unknown>("/admin/orders", { query: q }).then((r) => ({ items: asList<OrderSummary>(r), total: (r as Page<OrderSummary>)?.total ?? asList(r).length })),
  order: (id: string, signal?: AbortSignal) => request<AdminOrderDetail>(`/admin/orders/${id}`, { signal }),
  /** idempotencyKey estável por tentativa (o chamador guarda a chave até a ação concluir) para retry após timeout não repetir a ação. */
  orderAction: (id: string, action: AdminOrderAction, note?: string, idempotencyKey: string = newIdempotencyKey()) => request<unknown>(`/admin/orders/${id}/actions`, { method: "POST", body: { action, note }, idempotencyKey }),
  companies: () => request<unknown>("/admin/companies").then((r) => asList<Company>(r)),
  updateCompany: (id: string, data: { perTxLimitCents?: string; monthlyLimitCents?: string; takeRateOverrideBp?: number | null; betaEndsAt?: string | null; blocked?: boolean }) => request<unknown>(`/admin/companies/${id}`, { method: "PATCH", body: data }),
  reconciliationRuns: () => request<unknown>("/admin/reconciliation/runs").then((r) => asList<ReconciliationRun>(r)),
  reconciliationRun: (day?: string) => request<unknown>("/admin/reconciliation/run", { method: "POST", body: day ? { day } : {}, idempotencyKey: newIdempotencyKey() }),
  reconciliationIssues: (open = true) => request<unknown>("/admin/reconciliation/issues", { query: { open } }).then((r) => asList<ReconciliationIssue>(r)),
  resolveIssue: (id: string, resolution: string) => request<unknown>(`/admin/reconciliation/issues/${id}/resolve`, { method: "POST", body: { resolution }, idempotencyKey: newIdempotencyKey() }),
  setting: (key: SettingKey) => request<Setting>(`/admin/settings/${key}`),
  putSetting: (key: SettingKey, value: unknown, note: string) => request<Setting>(`/admin/settings/${key}`, { method: "PUT", body: { value, note } }),
  auditLog: (q?: { target?: string; from?: string; to?: string }) => request<unknown>("/admin/audit-log", { query: q }).then((r) => asList<AuditEntry>(r)),
  deadJobs: () => request<unknown>("/admin/jobs", { query: { status: "dead" } }).then((r) => asList<{ id: string; type: string; attempts: number; lastError?: string; runAt: string }>(r)),
  retryJob: (id: string) => request<unknown>(`/admin/jobs/${id}/retry`, { method: "POST", idempotencyKey: `retry:${id}:${Math.floor(Date.now() / 60_000)}` }),
};

export const api = { auth, companies, beneficiaries, quotes, orders, documents, notifications, sandbox, admin };
export type Api = typeof api;
